import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../utils/api";
import { logout as authLogout } from "../services/dataService";
import {
  playCheckoutSuccess,
  playSaveSuccess,
  playScanSuccess,
  playScanError,
} from "../utils/sounds";
import Sidebar, { getSidebarMenuAction } from "./Sidebar";
import WorkspaceHeader from "./WorkspaceHeader";
import CurrencyInput from "./CurrencyInput";
import ReceiptPrintLayout from "./ReceiptPrintLayout";
import { useToast } from "../contexts/ToastContext";
import { useConfirmation } from "../contexts/ConfirmationContext";
import {
  addOrIncrementCartEntry,
  applySavedStock,
  buildScannedCartOption,
  getCartEntryKey,
  resolveLoadedCashierBarcode,
  loadCashierCartState,
  parseStockDraft,
  removeCartEntry,
  saveCashierCartState,
  updateCartEntryQuantity,
} from "../utils/cashierStock";
import { formatCheckoutWarning } from "../utils/inventoryUnits";
import { usePressRepeat } from "../utils/pressRepeat";
import { getPendingCashierScan } from "./GlobalBarcodeScanner";
import SharedBarcodePicker from "./SharedBarcodePicker";

// Format number with commas and no decimals
const formatCurrency = (value) => {
  return Math.round(parseFloat(value) || 0).toLocaleString("en-US");
};

const CART_LABELS = ["Counter 1", "Counter 2"];
const getCartDomKey = (item) =>
  getCartEntryKey(item).replace(/[^a-z0-9_-]/gi, "-");

export const revealScannedCartEntry = (row, previousRow) => {
  if (!row) return previousRow || null;
  previousRow?.classList.remove("cashier-cart-row-scanned");
  row.classList.remove("cashier-cart-row-scanned");
  void row.offsetWidth;
  row.classList.add("cashier-cart-row-scanned");
  row.scrollIntoView({ block: "nearest" });
  return row;
};

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
};

const getFocusableElements = (container) =>
  Array.from(
    container?.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ) || [],
  );

const trapDialogFocus = (event, container, onClose) => {
  if (event.key === "Escape") {
    event.preventDefault();
    onClose();
    return;
  }
  if (event.key !== "Tab") return;

  const focusable = getFocusableElements(container);
  if (focusable.length === 0) {
    event.preventDefault();
    container?.focus();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const QuantityIncreaseButton = ({ onIncrease }) => {
  const { isPressed, pressProps } = usePressRepeat(onIncrease);

  return (
    <button
      type="button"
      className={`qty-btn qty-btn-repeat${isPressed ? " is-pressing" : ""}`}
      aria-label="Increase quantity"
      {...pressProps}
    >
      +
    </button>
  );
};

function Cashier() {
  const navigate = useNavigate();
  const location = useLocation();
  const routedScanHandledRef = useRef(null);
  const [initialCashierState] = useState(() => {
    const storedUser = readStoredUser();
    return {
      user: storedUser,
      cartState: loadCashierCartState(window.localStorage, storedUser),
    };
  });
  const [user, setUser] = useState(initialCashierState.user);

  // ── Multi-cart state ─────────────────────────────────────────────────────
  // carts[0] and carts[1] are independent — each cashier uses their own tab.
  // activeCart controls which cart receives scans and is displayed.
  const [carts, setCarts] = useState(initialCashierState.cartState.carts);
  const [activeCart, setActiveCart] = useState(
    initialCashierState.cartState.activeCart,
  );
  // Keep a ref so async processBarcode always reads the current value
  const activeCartRef = useRef(initialCashierState.cartState.activeCart);
  const processBarcodeRef = useRef(null);
  useEffect(() => { activeCartRef.current = activeCart; }, [activeCart]);
  useEffect(() => {
    saveCashierCartState(window.localStorage, user, { carts, activeCart });
  }, [activeCart, carts, user]);

  const [barcodeInput, setBarcodeInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [itemSearch, setItemSearch] = useState("");
  const [allItems, setAllItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [showItemBrowser, setShowItemBrowser] = useState(false);
  const [leftTab, setLeftTab] = useState("scan"); // "scan" | "edit"
  const [stockDrafts, setStockDrafts] = useState({});
  const [savingStockBarcode, setSavingStockBarcode] = useState(null);
  const [sharedBarcodeChoice, setSharedBarcodeChoice] = useState(null);
  const sharedBarcodeChoiceRef = useRef(null);
  const showToast = useToast();
  const showConfirmation = useConfirmation();

  // Receipt overlay — per-cart: { cartIndex, transactionId, totalAmount, items }
  const [receipt, setReceipt] = useState(null);
  const receiptDialogRef = useRef(null);
  const receiptReturnFocusRef = useRef(null);

  const barcodeInputRef = useRef(null);
  const cartRowRefs = useRef(new Map());
  const pendingScannedEntryByCart = useRef([null, null]);
  const highlightedRowByCart = useRef([null, null]);
  // Per-cart duplicate-scan tracking so Counter 1 and Counter 2 don't
  // interfere with each other's guards.
  const lastScannedBarcode = useRef({ 0: null, 1: null });
  const lastScanTime = useRef({ 0: 0, 1: 0 });
  const isProcessing = useRef(false);
  const scanDebounceTimer = useRef(null);

  useEffect(() => {
    sharedBarcodeChoiceRef.current = sharedBarcodeChoice;
  }, [sharedBarcodeChoice]);

  useEffect(() => {
    const entryKey = pendingScannedEntryByCart.current[activeCart];
    if (!entryKey) return;
    const row = cartRowRefs.current.get(entryKey);
    if (!row) return;

    highlightedRowByCart.current[activeCart] = revealScannedCartEntry(
      row,
      highlightedRowByCart.current[activeCart],
    );
    pendingScannedEntryByCart.current[activeCart] = null;
  }, [activeCart, carts]);

  useEffect(() => {
    if (!receipt) return undefined;
    receiptReturnFocusRef.current = document.activeElement;
    const frame = window.requestAnimationFrame(() => {
      getFocusableElements(receiptDialogRef.current)[0]?.focus();
    });
    return () => {
      window.cancelAnimationFrame(frame);
      receiptReturnFocusRef.current?.focus?.();
    };
  }, [receipt]);

  useEffect(() => {
    const userData = readStoredUser();
    setUser(userData);
    setTimeout(() => barcodeInputRef.current?.focus(), 200);

    // Load all items for backup item browser
    const loadItems = async () => {
      try {
        setItemsLoading(true);
        const response = await api.get("/items/cashier");
        setAllItems(response.data || []);
      } catch (err) {
        console.error("Failed to load items:", err);
      } finally {
        setItemsLoading(false);
      }
    };
    loadItems();
  }, []);

  // Global scanner capture — redirect keystrokes to barcode input
  // when focus is not already on an input/textarea/select
  useEffect(() => {
    const globalBuffer = { value: "", timer: null };

    const handleGlobalKey = (e) => {
      if (sharedBarcodeChoiceRef.current) return;
      const tag = document.activeElement?.tagName?.toLowerCase();
      const isInputFocused = tag === "input" || tag === "textarea" || tag === "select";
      if (isInputFocused) return; // let normal focus handle it
      if (e.key === "Enter") {
        if (globalBuffer.value.trim().length >= 4) {
          clearTimeout(globalBuffer.timer);
          processBarcodeRef.current?.(globalBuffer.value.trim());
          globalBuffer.value = "";
        }
        return;
      }
      // Only capture printable single characters
      if (e.key.length === 1) {
        globalBuffer.value += e.key;
        clearTimeout(globalBuffer.timer);
        globalBuffer.timer = setTimeout(() => {
          if (globalBuffer.value.trim().length >= 4) {
            processBarcodeRef.current?.(globalBuffer.value.trim());
          }
          globalBuffer.value = "";
        }, 80);
      }
    };

    document.addEventListener("keydown", handleGlobalKey);
    return () => document.removeEventListener("keydown", handleGlobalKey);
  }, []);

  // ── Cart helpers ─────────────────────────────────────────────────────────
  const getCart = (idx = activeCart) => carts[idx];

  const calculateTotal = (idx = activeCart) =>
    carts[idx].reduce((sum, item) => sum + item.totalPrice, 0);

  // ── Add a single item entry to a cart ────────────────────────────────────
  const addItemToCart = (entry, targetCart) => {
    pendingScannedEntryByCart.current[targetCart] = getCartEntryKey(entry);
    setCarts((prevCarts) =>
      prevCarts.map((cart, idx) => {
        if (idx !== targetCart) return cart;
        return addOrIncrementCartEntry(cart, entry);
      })
    );
  };

  const openSharedBarcodePicker = (barcode, options, targetCart) => {
    setSharedBarcodeChoice({ barcode, options, targetCart });
  };

  const closeSharedBarcodePicker = () => {
    setSharedBarcodeChoice(null);
    setTimeout(() => barcodeInputRef.current?.focus(), 100);
  };

  const handleSharedBarcodeSelect = (option) => {
    const targetCart = sharedBarcodeChoice?.targetCart;
    if (targetCart === undefined) return;
    addItemToCart(option, targetCart);
    setSharedBarcodeChoice(null);
    playScanSuccess();
    setTimeout(() => barcodeInputRef.current?.focus(), 100);
  };

  // ── Scan processing ──────────────────────────────────────────────────────
  const processBarcode = async (barcode) => {
    if (!barcode || !barcode.trim()) return;
    if (sharedBarcodeChoiceRef.current) return;

    const barcodeValue = barcode.trim();
    const now = Date.now();
    // Capture which cart is active at the moment of this scan
    const targetCart = activeCartRef.current;

    // Duplicate-scan guard — per cart so Counter 1 and Counter 2 are independent.
    // Same barcode within 500ms on the SAME counter = hardware double-fire, ignored.
    // Same barcode on a DIFFERENT counter, or after 500ms = intentional, allowed.
    if (
      lastScannedBarcode.current[targetCart] === barcodeValue &&
      now - lastScanTime.current[targetCart] < 500
    ) return;

    lastScannedBarcode.current[targetCart] = barcodeValue;
    lastScanTime.current[targetCart] = now;
    isProcessing.current = true;

    setBarcodeInput("");
    setLoading(true);
    let openedSharedPicker = false;

    try {
      const cachedMatch = resolveLoadedCashierBarcode(allItems, barcodeValue);
      if (cachedMatch) {
        const options = cachedMatch.options.map(buildScannedCartOption);
        if (cachedMatch.requiresChoice) {
          openedSharedPicker = true;
          openSharedBarcodePicker(barcodeValue, options, targetCart);
          return;
        }
        addItemToCart(options[0], targetCart);
        playScanSuccess();
        return;
      }

      const response = await api.get(`/sales/scan/${barcodeValue}`);
      const item = response.data;

      if (item.requiresChoice) {
        openedSharedPicker = true;
        openSharedBarcodePicker(
          barcodeValue,
          item.options.map(buildScannedCartOption),
          targetCart,
        );
        return;
      }

      if (item.isProductUnit) {
        addItemToCart(buildScannedCartOption(item), targetCart);
        playScanSuccess();
        return;
      }

      addItemToCart(buildScannedCartOption(item), targetCart);
      playScanSuccess();
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        "Item not found. Please add it in Admin section first.";
      showToast(errorMsg, "error");
      playScanError();
    } finally {
      setLoading(false);
      isProcessing.current = false;
      if (!openedSharedPicker) {
        setTimeout(() => barcodeInputRef.current?.focus(), 100);
      }
    }
  };
  processBarcodeRef.current = processBarcode;

  useEffect(() => {
    const pendingScan = getPendingCashierScan(
      location.state,
      routedScanHandledRef.current,
    );
    if (!pendingScan) return;

    routedScanHandledRef.current = pendingScan.scanId;
    navigate("/cashier", { replace: true, state: null });
    processBarcodeRef.current?.(pendingScan.barcode);
  }, [location.state, navigate]);

  const handleBarcodeScan = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (barcodeInput.trim()) await processBarcode(barcodeInput);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setBarcodeInput(value);

    // Auto-submit on newline/tab (some scanners send these)
    if (value.length > 0 && (value.endsWith("\n") || value.endsWith("\r") || value.endsWith("\t"))) {
      const cleanValue = value.trim();
      if (cleanValue) { clearTimeout(scanDebounceTimer.current); processBarcode(cleanValue); }
      return;
    }

    // Auto-submit after a brief pause — scanners type all chars instantly,
    // so a short pause means the scan is complete
    if (value.trim().length >= 4) {
      clearTimeout(scanDebounceTimer.current);
      scanDebounceTimer.current = setTimeout(() => {
        const current = barcodeInputRef.current?.value?.trim();
        if (current && current.length >= 4) processBarcode(current);
      }, 80);
    }
  };

  const addItemFromBrowser = (item) => {
    const itemId = item.itemId ?? item.id;
    addItemToCart({ ...item, itemId }, activeCart);
    playScanSuccess();
    showToast(`Added ${item.name}`, "success");
  };

  const filteredItems = allItems.filter(
    (item) =>
      item.name.toLowerCase().includes(itemSearch.toLowerCase()) ||
      item.barcode.toLowerCase().includes(itemSearch.toLowerCase())
  );

  // ── Per-cart operations ──────────────────────────────────────────────────
  const updateQuantity = (cartIdx, entryKey, change) => {
    setCarts((prev) =>
      prev.map((cart, idx) => {
        if (idx !== cartIdx) return cart;
        return updateCartEntryQuantity(cart, entryKey, change);
      })
    );
  };

  const removeItem = (cartIdx, entryKey) => {
    setCarts((prev) =>
      prev.map((cart, idx) =>
        idx !== cartIdx
          ? cart
          : removeCartEntry(cart, entryKey)
      )
    );
  };

  const handleSaveStock = async (item) => {
    const entryKey = getCartEntryKey(item);
    const draft = stockDrafts[entryKey] ?? String(item.stock ?? 0);
    const parsed = parseStockDraft(draft);
    if (parsed.error) {
      showToast(parsed.error, "error");
      return;
    }

    setSavingStockBarcode(entryKey);
    try {
      const response = await api.post("/items/stock", {
        itemId: item.itemId,
        barcode: item.barcode,
        stock: parsed.value,
      });
      const savedStock = response.data.stock;
      setCarts((previous) =>
        applySavedStock(previous, item.barcode, savedStock, {
          itemId: item.itemId,
        }),
      );
      setAllItems((previous) =>
        previous.map((entry) =>
          String(entry.itemId ?? entry.id) === String(item.itemId)
            ? { ...entry, stock: savedStock }
            : entry,
        ),
      );
      setStockDrafts((previous) => ({
        ...previous,
        [entryKey]: String(savedStock),
      }));
      playSaveSuccess();
      showToast(`Stock updated to ${savedStock} for ${item.name}`, "success");
    } catch (err) {
      showToast(err.response?.data?.error || "Failed to update stock", "error");
    } finally {
      setSavingStockBarcode(null);
    }
  };

  const clearCart = (cartIdx) => {
    setCarts((prev) => prev.map((cart, idx) => (idx !== cartIdx ? cart : [])));
  };

  const confirmClearCart = (cartIdx) => {
    showConfirmation(
      `Clear ${CART_LABELS[cartIdx]}?`,
      "This will remove every item from this cart.",
      () => clearCart(cartIdx)
    );
  };

  // ── Checkout ─────────────────────────────────────────────────────────────
  const handleCheckout = async (cartIdx) => {
    const cart = carts[cartIdx];
    if (cart.length === 0) { showToast("Cart is empty", "error"); return; }

    setLoading(true);

    try {
      const response = await api.post("/sales/checkout", { items: cart });
      const remainingStock = response.data.remainingStock || [];
      const checkoutWarnings = response.data.warnings || [];
      setReceipt({
        cartIndex: cartIdx,
        transactionId: response.data.transactionId,
        totalAmount: response.data.totalAmount,
        items: response.data.items?.length ? response.data.items : cart,
      });
      setCarts((previous) => {
        const withFreshStock = remainingStock.reduce(
          (current, result) =>
            applySavedStock(current, result.barcode, result.stock, {
              unitId: result.unitId,
              itemId: result.itemId,
            }),
          previous,
        );
        return withFreshStock.map((current, index) =>
          index === cartIdx ? [] : current,
        );
      });
      setAllItems((previous) =>
        previous.map((entry) => {
          const result = remainingStock.find(
            (candidate) => candidate.unitId != null
              ? String(candidate.unitId) === String(entry.unitId)
              : candidate.itemId != null
                ? String(candidate.itemId) === String(entry.itemId ?? entry.id)
                : String(candidate.barcode) === String(entry.barcode),
          );
          return result ? { ...entry, stock: result.stock } : entry;
        }),
      );
      setStockDrafts((previous) => {
        const next = { ...previous };
        remainingStock.forEach((result) => {
          next[getCartEntryKey(result)] = String(result.stock);
        });
        return next;
      });
      playCheckoutSuccess();
      checkoutWarnings.forEach((warning) => {
        showToast(formatCheckoutWarning(warning), "warning");
      });
    } catch (err) {
      const shortages = err.response?.data?.details?.shortages;
      if (Array.isArray(shortages) && shortages.length) {
        showToast(
          `Checkout blocked: ${shortages
            .map((line) => `${line.product} needs ${line.requiredDeduction}, recorded ${line.stockBefore}`)
            .join("; ")}`,
          "error",
        );
      } else {
        showToast(err.response?.data?.error || "Failed to process checkout", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReceipt = () => window.print();

  const handleCloseReceipt = () => {
    setReceipt(null);
  };

  // ── Sidebar ──────────────────────────────────────────────────────────────
  const handleLogout = async () => {
    await authLogout();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mobileMenuButtonRef = useRef(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true"
  );

  const handleToggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("sidebarCollapsed", String(next));
      return next;
    });
  };

  const handleOpenMenu = () => {
    const action = getSidebarMenuAction(window.innerWidth, sidebarCollapsed);
    if (action === "open") {
      setSidebarOpen(true);
      return;
    }
    if (action === "expand") handleToggleSidebar();
  };

  // ── Resizable cart panel ─────────────────────────────────────────────────
  const [cartWidth, setCartWidth] = useState(500);
  const isResizing = useRef(false);
  const layoutRef = useRef(null);

  const handleResizeStart = (e) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMove = (moveEvent) => {
      if (!isResizing.current || !layoutRef.current) return;
      const layoutRect = layoutRef.current.getBoundingClientRect();
      const clientX = moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX;
      // Cart width = right edge of layout minus pointer position
      const newWidth = layoutRect.right - clientX;
      // Clamp between 300px and 60% of layout width
      const clamped = Math.min(
        Math.max(newWidth, 300),
        layoutRect.width * 0.6
      );
      setCartWidth(Math.round(clamped));
    };

    const onUp = () => {
      isResizing.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const activeCartItems = getCart(activeCart);
  const activeTotal = calculateTotal(activeCart);
  const activeTotalItems = activeCartItems.reduce((s, i) => s + i.quantity, 0);

  return (
    <div className="app-layout">
      <Sidebar
        user={user}
        onLogout={handleLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
        menuButtonRef={mobileMenuButtonRef}
      />
      <main className={`main-content ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
        {/* Mobile Menu Button */}
        <button
          ref={mobileMenuButtonRef}
          className="mobile-menu-btn"
          onClick={handleOpenMenu}
          aria-label="Open menu"
          aria-controls="app-sidebar"
          aria-expanded={sidebarOpen}
        >
          <span></span>
        </button>

        <WorkspaceHeader
          workspaceLabel="Cashier"
          pageLabel="Sales counter"
          stationLabel="Front counter"
          user={user}
        />

        {/* ── Receipt overlay (modal) ── */}
        {receipt && (
          <div className="modal-overlay">
            <div
              ref={receiptDialogRef}
              className="modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="receipt-dialog-title"
              tabIndex={-1}
              onKeyDown={(event) => trapDialogFocus(event, receiptDialogRef.current, handleCloseReceipt)}
            >
              <div className="modal-header">
                <h2 id="receipt-dialog-title">Receipt — {CART_LABELS[receipt.cartIndex]}</h2>
                <button
                  type="button"
                  className="close-btn"
                  onClick={handleCloseReceipt}
                  aria-label="Close receipt"
                >
                  &times;
                </button>
              </div>
              <ReceiptPrintLayout receipt={receipt} formatCurrency={formatCurrency} />
              <div className="receipt-actions" style={{ padding: "0 var(--sp-5) var(--sp-5)" }}>
                <button className="btn btn-secondary" onClick={handlePrintReceipt}>
                  Print Receipt
                </button>
                <button className="btn btn-primary" onClick={handleCloseReceipt}>
                  Close &amp; Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {sharedBarcodeChoice && (
          <SharedBarcodePicker
            barcode={sharedBarcodeChoice.barcode}
            options={sharedBarcodeChoice.options}
            onSelect={handleSharedBarcodeSelect}
            onCancel={closeSharedBarcodePicker}
          />
        )}

        {/* ── Main cashier shell ── */}
        <div className="cashier-shell">
          <section className="cashier-topbar card">
            <div>
              <div className="cashier-topbar-kicker">Sales counter</div>
              <h1 className="cashier-topbar-title">Live checkout terminal</h1>
              <p className="cashier-topbar-copy">
                Scan items, watch the cart, and close the sale without dashboard clutter.
              </p>
            </div>
            <div className="cashier-topbar-metrics">
              <div className="cashier-metric">
                <span className="cashier-metric-label">Cashier</span>
                <strong>{user?.fullName || user?.username || "Active user"}</strong>
              </div>
              <div className="cashier-metric">
                <span className="cashier-metric-label">Items</span>
                <strong>{activeTotalItems}</strong>
              </div>
              <div className="cashier-metric">
                <span className="cashier-metric-label">Total</span>
                <strong>MMK {formatCurrency(activeTotal)}</strong>
              </div>
            </div>
          </section>

          <div className="cashier-layout" ref={layoutRef}>
            {/* ── Left: scan input ── */}
            <div className="cashier-left-column">
              <div className="card cashier-scan-card cashier-panel">
                <div className="cashier-panel-header">
                  <div>
                    <div className="cashier-panel-kicker">Input — {CART_LABELS[activeCart]}</div>
                    <h2>{leftTab === "scan" ? "Item scanning" : "Edit cart"}</h2>
                    <p>{leftTab === "scan" ? "Use the camera, a USB scanner, or direct barcode entry." : "Override price or type quantity directly."}</p>
                  </div>
                  <div className="cashier-panel-chip">
                    {loading ? "Processing" : "Ready"}
                  </div>
                </div>

                {/* Tab switcher */}
                <div style={{ display: "flex", gap: "var(--sp-2)", marginBottom: "var(--sp-4)", borderBottom: "1px solid var(--clr-border)", paddingBottom: "var(--sp-3)" }}>
                  <button
                    className={`btn ${leftTab === "scan" ? "btn-primary" : "btn-secondary"}`}
                    style={{ flex: 1 }}
                    onClick={() => { setLeftTab("scan"); setTimeout(() => barcodeInputRef.current?.focus(), 100); }}
                  >
                    Scan
                  </button>
                  <button
                    className={`btn ${leftTab === "edit" ? "btn-primary" : "btn-secondary"}`}
                    style={{ flex: 1 }}
                    onClick={() => setLeftTab("edit")}
                  >
                    Edit Cart
                  </button>
                </div>

                {leftTab === "edit" && (
                  <div className="cashier-edit-cart-list">
                    {getCart().length === 0 ? (
                      <div style={{ textAlign: "center", color: "var(--clr-muted)", padding: "var(--sp-6)" }}>
                        No items in cart yet.
                      </div>
                    ) : getCart().map((item, idx) => (
                      <div key={getCartEntryKey(item)} className="cashier-edit-cart-row">
                        <div className="cashier-edit-cart-name">{item.name}</div>
                        <div className="cashier-edit-cart-fields">
                          <div className="cashier-edit-cart-field">
                          <label htmlFor={`cart-${activeCart}-${getCartDomKey(item)}-price`} style={{ fontSize: "10px", color: "var(--clr-muted)", textTransform: "uppercase" }}>Price</label>
                          <CurrencyInput
                            id={`cart-${activeCart}-${getCartDomKey(item)}-price`}
                            className="numeric-entry"
                            min={0}
                            step={50}
                            value={item.unitPrice}
                            label="cart price"
                            style={{ width: "110px" }}
                            onChange={(value) => {
                              const price = parseFloat(value) || 0;
                              setCarts((prev) => prev.map((cart, ci) => ci !== activeCart ? cart : cart.map((c, i) => i !== idx ? c : { ...c, unitPrice: price, totalPrice: price * c.quantity })));
                            }}
                          />
                          </div>
                          <div className="cashier-edit-cart-field">
                            <label htmlFor={`cart-${activeCart}-${getCartDomKey(item)}-stock`}>
                              Stock
                            </label>
                            <div className="cashier-stock-save-group">
                              <input
                                id={`cart-${activeCart}-${getCartDomKey(item)}-stock`}
                                className="numeric-entry cashier-stock-input"
                                type="number"
                                min="0"
                                step="1"
                                inputMode="numeric"
                                value={
                                  stockDrafts[getCartEntryKey(item)] ??
                                  String(item.stock ?? 0)
                                }
                                onChange={(event) =>
                                  setStockDrafts((previous) => ({
                                    ...previous,
                                    [getCartEntryKey(item)]: event.target.value,
                                  }))
                                }
                              />
                              <button
                                type="button"
                                className="btn btn-secondary cashier-save-stock-btn"
                                onClick={() => handleSaveStock(item)}
                                disabled={savingStockBarcode === getCartEntryKey(item)}
                              >
                                {savingStockBarcode === getCartEntryKey(item)
                                  ? "Saving…"
                                  : "Save Stock"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {leftTab === "scan" && (
                  <>
                    <div className="cashier-entry-section">
                      <div className="form-group cashier-entry-group">
                        <label>Barcode entry</label>
                        <input
                          ref={barcodeInputRef}
                          type="text"
                          className="scan-input"
                          placeholder="Type barcode or scan into this field"
                          value={barcodeInput}
                          onChange={handleInputChange}
                          onKeyDown={handleBarcodeScan}
                          disabled={loading}
                          autoComplete="off"
                          autoFocus
                        />
                      </div>
                      <div className="cashier-help-strip">
                        <span>Press Enter after typing a code.</span>
                        <span>Repeated scans increase quantity.</span>
                      </div>
                    </div>

                    {/* Item Browser (Backup) */}
                    <div style={{ borderTop: "1px solid var(--clr-border)", marginTop: "var(--sp-5)", paddingTop: "var(--sp-5)" }}>
                      <button
                        className="btn btn-secondary"
                        style={{ width: "100%", marginBottom: "var(--sp-3)" }}
                        onClick={() => setShowItemBrowser(!showItemBrowser)}
                      >
                        {showItemBrowser ? "Hide" : "Show"} Item Browser
                      </button>

                      {showItemBrowser && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
                          <input
                            type="text"
                            className="scan-input"
                            placeholder="Search by name or barcode..."
                            value={itemSearch}
                            onChange={(e) => setItemSearch(e.target.value)}
                            style={{ fontSize: "14px" }}
                          />

                          {itemsLoading ? (
                            <div style={{ padding: "var(--sp-5)", textAlign: "center", color: "#94A3B8" }}>
                              Loading items...
                            </div>
                          ) : filteredItems.length === 0 ? (
                            <div style={{ padding: "var(--sp-5)", textAlign: "center", color: "#94A3B8" }}>
                              {allItems.length === 0 ? "No items available" : "No matches found"}
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(2, 1fr)",
                                gap: "8px",
                                maxHeight: "300px",
                                overflowY: "auto",
                                border: "1px solid var(--clr-border)",
                                borderRadius: "4px",
                                padding: "8px",
                              }}
                            >
                              {filteredItems.map((item) => (
                                <button
                                  key={item.browserKey || `${item.id}-${item.barcode}`}
                                  className="item-browser-btn"
                                  onClick={() => addItemFromBrowser(item)}
                                >
                                  <div style={{ fontWeight: 500, fontSize: "13px", lineHeight: "1.2" }}>
                                    {item.name}
                                  </div>
                                  <div style={{ fontSize: "11px", opacity: 0.8, marginTop: "4px" }}>
                                    MMK {formatCurrency(item.price)}
                                  </div>
                                  {item.isProductUnit && (
                                    <div style={{ fontSize: "11px", opacity: 0.8 }}>
                                      {item.availableUnits} {item.unitName} available · {item.stock} base units
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── Drag handle ── */}
            <div
              className="cashier-resize-handle"
              onMouseDown={handleResizeStart}
              onTouchStart={handleResizeStart}
              title="Drag to resize"
            />

            {/* ── Right: cart panel ── */}
            <div className="cashier-right-column" style={{ width: cartWidth, flexShrink: 0 }}>
              <div className="card cashier-cart-card cashier-panel">

                {/* Cart tabs */}
                <div className="cart-tabs">
                  {CART_LABELS.map((label, idx) => {
                    const tabItems = carts[idx].reduce((s, i) => s + i.quantity, 0);
                    return (
                      <button
                        key={idx}
                        className={`cart-tab ${activeCart === idx ? "active" : ""}`}
                        onClick={() => setActiveCart(idx)}
                      >
                        {label}
                        {tabItems > 0 && (
                          <span className="cart-tab-badge">{tabItems}</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="cashier-panel-header">
                  <div>
                    <div className="cashier-panel-kicker">Current order</div>
                    <h2>Cart summary</h2>
                    <p>Review line items before payment.</p>
                  </div>
                  <div className="cashier-cart-header-actions">
                    <div className="cashier-cart-total">
                      MMK {formatCurrency(activeTotal)}
                    </div>
                    {activeCartItems.length > 0 && (
                      <button
                        type="button"
                        className="cart-clear-btn"
                        onClick={() => confirmClearCart(activeCart)}
                        aria-label={`Clear all items from ${CART_LABELS[activeCart]}`}
                        title={`Clear all items from ${CART_LABELS[activeCart]}`}
                      >
                        <svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18" />
                          <path d="M8 6V4h8v2" />
                          <path d="M19 6l-1 14H6L5 6" />
                          <path d="M10 11v5M14 11v5" />
                        </svg>
                        <span>Clear cart</span>
                      </button>
                    )}
                  </div>
                </div>

                {activeCartItems.length === 0 ? (
                  <div className="cashier-empty-state">
                    <div className="cashier-empty-mark">ORDER</div>
                    <h3>No items in the cart</h3>
                    <p>Start scanning from the left panel to build the current sale.</p>
                  </div>
                ) : (
                  <div className="cashier-cart-body">
                    <div className="cashier-cart-table-wrap">
                      <table className="cashier-cart-table">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Price</th>
                            <th>Qty</th>
                            <th>Total</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeCartItems.map((item) => {
                            const entryKey = getCartEntryKey(item);
                            return (
                              <tr
                                key={entryKey}
                                ref={(row) => {
                                  if (row) cartRowRefs.current.set(entryKey, row);
                                  else cartRowRefs.current.delete(entryKey);
                                }}
                              >
                                <td>
                                  <div>
                                    <strong>{item.name}</strong>
                                    <div className="text-muted font-mono" style={{ fontSize: "11px" }}>
                                      {item.barcode}
                                    </div>
                                    {item.isProductUnit && (
                                      <div className="text-muted" style={{ fontSize: "11px" }}>
                                        {item.unitName} · {item.conversionFactor} base units each · {item.availableUnits} available
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>MMK {formatCurrency(item.unitPrice)}</td>
                                <td>
                                  <div className="qty-ctrl">
                                    <button
                                      type="button"
                                      className="qty-btn"
                                      onClick={() => updateQuantity(activeCart, entryKey, -1)}
                                      disabled={item.quantity <= 1}
                                      aria-label={item.quantity <= 1 ? "Quantity cannot be less than 1; use Remove item to delete it" : "Decrease quantity"}
                                    >−</button>
                                    <span className="qty-value">{item.quantity}</span>
                                    <QuantityIncreaseButton
                                      onIncrease={() =>
                                        updateQuantity(activeCart, entryKey, 1)
                                      }
                                    />
                                  </div>
                                </td>
                                <td><strong>MMK {formatCurrency(item.totalPrice)}</strong></td>
                                <td>
                                  <button
                                    type="button"
                                    className="cart-remove-btn"
                                    onClick={() => removeItem(activeCart, entryKey)}
                                    aria-label={`Remove ${item.name} from ${CART_LABELS[activeCart]}`}
                                    title="Remove item"
                                  >✕</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="cashier-cart-footer">
                      <div className="cashier-total-row">
                        <span className="cashier-total-label">Lines</span>
                        <span style={{ fontWeight: 600 }}>{activeCartItems.length}</span>
                      </div>
                      <div className="cashier-total-row">
                        <span className="cashier-total-label">Units</span>
                        <span style={{ fontWeight: 600 }}>{activeTotalItems}</span>
                      </div>
                      <div
                        className="cashier-total-row"
                        style={{ paddingTop: "8px", borderTop: "1px solid var(--clr-border)", marginTop: "4px" }}
                      >
                        <span className="cashier-total-label">Amount Due</span>
                        <span className="cashier-total-amount">MMK {formatCurrency(activeTotal)}</span>
                      </div>
                      <button
                        className="btn btn-primary checkout-btn"
                        onClick={() => handleCheckout(activeCart)}
                        disabled={activeCartItems.length === 0 || loading}
                      >
                        {loading ? "Processing..." : `Checkout ${CART_LABELS[activeCart]}`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

    </div>
  );
}

export default Cashier;
